import React, { useState, useEffect } from 'react';
import ManagerSidebar from '../components/Sidebar';
import { useSidebar } from '../context/SidebarContext';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import {
    collection,
    onSnapshot,
    query,
    where,
    addDoc,
    serverTimestamp,
    orderBy,
    doc,
    updateDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';

function ManagerQueries() {
    const { currentUser, userData } = useAuth();
    const [queries, setQueries] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const { isMobile, isOpen, toggleMobileSidebar } = useSidebar();
    const [isLoading, setIsLoading] = useState(true);
    const [selectedQuery, setSelectedQuery] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // New States for Detailed View
    const [invoices, setInvoices] = useState([]);


    // Report Issue States
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const [reportDescription, setReportDescription] = useState('');
    const [reportType, setReportType] = useState('general'); // 'general' or 'followup'
    const [followUpImages, setFollowUpImages] = useState([]);
    const [isUploadingReport, setIsUploadingReport] = useState(false);

    const [newQuery, setNewQuery] = useState({
        serviceType: '',
        title: '',
        description: '',
        address: '',
        startDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        latitude: '',
        longitude: ''
    });

    // QUERY STATUS CONSTANTS
    const QUERY_STATUSES = {
        PENDING: 'Pending',
        ACCEPTED: 'Accepted',
        DRIVER_ASSIGNED: 'Driver Assigned',
        DRIVER_ACCEPTED: 'Driver Accepted',
        DRIVER_ON_WAY: 'Driver On Way',
        DRIVER_REACHED: 'Driver Reached',
        DRIVER_VERIFIED: 'Site Verified',
        DRIVER_REPORTED: 'Inspection Completed',
        QUOTATION_UPLOADED: 'Quotation Uploaded',
        QUOTATION_ACCEPTED: 'Quotation Accepted',
        QUOTATION_REJECTED: 'Quotation Rejected',
        TECHNICIAN_ASSIGNED: 'Technician Assigned',
        TECHNICIAN_ACCEPTED: 'Technician Accepted',
        TECHNICIAN_ON_WAY: 'Technician On Way',
        TECHNICIAN_REACHED: 'Technician Reached',
        TECHNICIAN_WORKING: 'Work in Progress',
        TECHNICIAN_COMPLETED: 'Work Completed',
        INVOICE_GENERATED: 'Invoice Generated',
        PAYMENT_UPLOADED: 'Payment Uploaded',
        PAID: 'Paid',
        CLOSED: 'Closed'
    };

    const getWorkflowProgress = (status) => {
        const statuses = [
            QUERY_STATUSES.PENDING,
            QUERY_STATUSES.ACCEPTED,
            QUERY_STATUSES.DRIVER_ASSIGNED,
            QUERY_STATUSES.DRIVER_ACCEPTED,
            QUERY_STATUSES.DRIVER_ON_WAY,
            QUERY_STATUSES.DRIVER_REACHED,
            QUERY_STATUSES.DRIVER_VERIFIED,
            QUERY_STATUSES.DRIVER_REPORTED,
            QUERY_STATUSES.QUOTATION_UPLOADED,
            QUERY_STATUSES.QUOTATION_ACCEPTED,
            QUERY_STATUSES.TECHNICIAN_ASSIGNED,
            QUERY_STATUSES.TECHNICIAN_ACCEPTED,
            QUERY_STATUSES.TECHNICIAN_ON_WAY,
            QUERY_STATUSES.TECHNICIAN_REACHED,
            QUERY_STATUSES.TECHNICIAN_WORKING,
            QUERY_STATUSES.TECHNICIAN_COMPLETED,
            QUERY_STATUSES.INVOICE_GENERATED,
            QUERY_STATUSES.PAYMENT_UPLOADED,
            QUERY_STATUSES.PAID,
            QUERY_STATUSES.CLOSED
        ];
        const index = statuses.indexOf(status);
        return index === -1 ? 0 : index;
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        if (timestamp.toDate) return timestamp.toDate().toLocaleString();
        return new Date(timestamp).toLocaleString();
    };

    const formatDateString = (dateVal) => {
        if (!dateVal) return 'No Date';
        if (dateVal.toDate) return dateVal.toDate().toLocaleDateString();
        try {
            return new Date(dateVal).toLocaleDateString();
        } catch (e) {
            return 'Invalid Date';
        }
    };

    // Follow Up Helpers
    const getFollowUpTimeRemaining = (query) => {
        if (!query?.followUpAllowedUntil) return "Expired";
        const now = new Date();
        const allowedUntil = query.followUpAllowedUntil.toDate ? query.followUpAllowedUntil.toDate() : new Date(query.followUpAllowedUntil);

        if (now > allowedUntil) return "Expired";

        const diffMs = allowedUntil - now;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

        return `${diffDays}d ${diffHours}h remaining`;
    };

    const canCreateFollowUp = (query) => {
        if (!query) return false;
        if (query.status !== 'Completed' && query.status !== 'Paid') return false; // Basic check
        // Check if within 14 days of completion (or any logic consistent with backend)
        if (query.followUpAllowedUntil) {
            const now = new Date();
            const allowedUntil = query.followUpAllowedUntil.toDate ? query.followUpAllowedUntil.toDate() : new Date(query.followUpAllowedUntil);
            return now <= allowedUntil;
        }
        return false; // Default if no date set
    };

    // Fetch Queries for this Manager
    useEffect(() => {
        if (!currentUser) return;

        // Mobile App Logic: Managers only see queries they created
        const q = query(
            collection(db, 'queries'),
            where('createdBy', '==', currentUser.uid)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const queriesList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            // Client-side sort
            queriesList.sort((a, b) => {
                const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
                const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
                return dateB - dateA;
            });
            setQueries(queriesList);
            setIsLoading(false);
        }, (error) => {
            console.error("Error fetching queries:", error);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser]);

    // Fetch Invoices when query selected
    useEffect(() => {
        if (!selectedQuery) {
            setInvoices([]);
            return;
        }

        const q = query(
            collection(db, 'queries', selectedQuery.id, 'invoices'),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setInvoices(snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })));
        });

        return () => unsubscribe();
    }, [selectedQuery]);

    // Handle Report/Follow Up Logic
    const handleReportIssue = async () => {
        if (!selectedQuery || !reportDescription.trim()) {
            alert("Please enter a description for the issue.");
            return;
        }

        setIsUploadingReport(true);
        try {
            if (reportType === 'followup') {
                // Update Follow Up subcollection/field on main query doc
                const followUpData = {
                    description: reportDescription,
                    images: followUpImages,
                    status: 'pending',
                    createdAt: new Date(),
                    createdBy: currentUser.uid,
                    createdRole: 'manager',
                    adminReply: null
                };

                await updateDoc(doc(db, 'queries', selectedQuery.id), {
                    followUp: followUpData,
                    // We don't change main status usually, or maybe set a flag
                });

                // Update local state
                setSelectedQuery(prev => ({
                    ...prev,
                    followUp: followUpData
                }));

                alert("Follow-up issue reported to Admin.");
            } else {
                // General Report
                await addDoc(collection(db, 'reports'), {
                    queryId: selectedQuery.id,
                    queryTitle: selectedQuery.title || selectedQuery.serviceType,
                    description: reportDescription,
                    reportedBy: currentUser.uid,
                    reportedRole: 'manager',
                    createdAt: serverTimestamp(),
                    status: 'open'
                });
                alert("Issue reported successfully to Admin.");
            }

            setIsReportModalOpen(false);
            setReportDescription('');
            setFollowUpImages([]);
            setReportType('general');

        } catch (error) {
            console.error("Error reporting issue:", error);
            alert("Failed to report issue: " + error.message);
        } finally {
            setIsUploadingReport(false);
        }
    };

    const handleUploadReportImage = async (e) => {
        if (!e.target.files?.length) return;
        setIsUploadingReport(true);
        try {
            const files = Array.from(e.target.files);
            const uploadPromises = files.map(async (file) => {
                const storageRef = ref(storage, `report-images/${selectedQuery.id}/${Date.now()}_${file.name}`);
                await uploadBytes(storageRef, file);
                return await getDownloadURL(storageRef);
            });
            const urls = await Promise.all(uploadPromises);
            setFollowUpImages(prev => [...prev, ...urls]);
        } catch (error) {
            console.error("Error uploading images:", error);
            alert("Failed to upload images");
        } finally {
            setIsUploadingReport(false);
        }
    };

    const handleDeleteReportImage = (url) => {
        setFollowUpImages(prev => prev.filter(img => img !== url));
    };

    // QUOTATION & PAYMENT HANDLERS
    const handleAcceptQuotation = async () => {
        if (!selectedQuery) return;
        if (!window.confirm("Accept this quotation?")) return;
        try {
            await updateDoc(doc(db, 'queries', selectedQuery.id), {
                status: QUERY_STATUSES.QUOTATION_ACCEPTED
            });
            setSelectedQuery(prev => ({ ...prev, status: QUERY_STATUSES.QUOTATION_ACCEPTED }));
        } catch (error) {
            console.error("Error accepting quotation:", error);
            alert("Failed to accept quotation");
        }
    };

    const handleRejectQuotation = async () => {
        if (!selectedQuery) return;
        if (!window.confirm("Reject this quotation?")) return;
        try {
            await updateDoc(doc(db, 'queries', selectedQuery.id), {
                status: QUERY_STATUSES.QUOTATION_REJECTED
            });
            setSelectedQuery(prev => ({ ...prev, status: QUERY_STATUSES.QUOTATION_REJECTED }));
        } catch (error) {
            console.error("Error rejecting quotation:", error);
            alert("Failed to reject quotation");
        }
    };

    const handleCreateQuery = async (e) => {
        e.preventDefault();
        try {
            const queryData = {
                partnerId: userData?.partnerId || '', // Manager should have partnerId in their profile
                partnerName: userData?.partnerName || 'Partner',
                serviceType: newQuery.serviceType,
                title: newQuery.title,
                description: newQuery.description,
                location: newQuery.address,
                latitude: parseFloat(newQuery.latitude) || 0,
                longitude: parseFloat(newQuery.longitude) || 0,
                startDate: newQuery.startDate,
                dueDate: newQuery.dueDate,
                status: 'Pending',
                createdBy: currentUser.uid,
                createdRole: 'manager',
                createdAt: serverTimestamp(),
            };

            await addDoc(collection(db, 'queries'), queryData);
            setIsModalOpen(false);
            setNewQuery({
                serviceType: '',
                title: '',
                description: '',
                address: '',
                startDate: new Date().toISOString().split('T')[0],
                dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                latitude: '',
                longitude: ''
            });
            alert("Query created successfully!");
        } catch (error) {
            console.error("Error creating query:", error);
            alert("Failed to create query");
        }
    };

    const getStatusColor = (status) => {
        switch ((status || '').toLowerCase()) {
            case 'completed': return 'bg-green-100 text-green-700';
            case 'in progress': return 'bg-blue-100 text-blue-700';
            case 'pending': return 'bg-yellow-100 text-yellow-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    const filteredQueries = queries.filter(q => {
        const matchesSearch = (q.title?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (q.description?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
            (q.serviceType?.toLowerCase() || '').includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || (q.status?.toLowerCase() || '') === statusFilter.toLowerCase();
        return matchesSearch && matchesStatus;
    });

    return (
        <div className="flex h-screen overflow-hidden bg-gray-50">
            <ManagerSidebar />
            <div
                className="flex-1 overflow-y-auto transition-all duration-300"
                style={{ marginLeft: isMobile ? 0 : (isOpen ? '16rem' : '5rem') }}
            >
                <div className="p-6 min-h-screen">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <div className="flex items-center">
                                {isMobile && (
                                    <button
                                        onClick={toggleMobileSidebar}
                                        className="mr-4 p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-brand-golden"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                        </svg>
                                    </button>
                                )}
                                <h1 className="text-3xl font-bold text-brand-dark">My Queries</h1>
                            </div>
                            <p className="text-gray-600 mt-1">Queries you have raised</p>
                        </div>
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="bg-brand-golden hover:bg-yellow-600 text-black px-4 py-2 rounded-lg font-medium flex items-center gap-2"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Create Query
                        </button>
                    </div>

                    {/* Search and Filters */}
                    <div className="bg-brand-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                        <div className="flex flex-col md:flex-row gap-4">
                            <div className="flex-1 relative">
                                <input
                                    type="text"
                                    placeholder="Search your queries..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-golden focus:border-transparent"
                                />
                            </div>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-golden focus:border-transparent"
                            >
                                <option value="all">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="in progress">In Progress</option>
                                <option value="completed">Completed</option>
                            </select>
                        </div>
                    </div>

                    {/* Queries List */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredQueries.map(item => (
                            <div key={item.id} className="bg-brand-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
                                <div className="flex justify-between items-start mb-4">
                                    <h3 className="font-bold text-lg text-brand-dark">{item.serviceType || item.title || 'Service Request'}</h3>
                                    <span className={`px-2 py-1 rounded text-xs font-bold ${getStatusColor(item.status)}`}>
                                        {item.status}
                                    </span>
                                </div>
                                <p className="text-gray-600 text-sm mb-2">{item.location || 'No location'}</p>
                                <p className="text-gray-500 text-sm mb-4 line-clamp-2">{item.description}</p>

                                <div className="flex justify-between items-center text-xs text-gray-400 border-t pt-4 mt-4">
                                    <span>{formatDateString(item.startDate)}</span>
                                    <button
                                        onClick={() => setSelectedQuery(item)}
                                        className="text-brand-golden hover:text-yellow-600 font-bold flex items-center gap-1"
                                    >
                                        View Details
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {filteredQueries.length === 0 && !isLoading && (
                        <div className="text-center py-12">
                            <p className="text-gray-500">No queries created yet</p>
                        </div>
                    )}
                </div>
            </div>

            {/* View Query Details Modal - Card Style */}
            {/* View Query Details Modal - Card Style */}
            {selectedQuery && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl flex flex-col md:flex-row overflow-hidden">

                        {/* Sidebar / Left Column (Basic Info) */}
                        <div className="w-full md:w-1/3 bg-gray-50 border-r border-gray-100 p-6 flex flex-col gap-6">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900 leading-tight mb-1">{selectedQuery.serviceType}</h2>
                                <p className="text-sm text-gray-500 font-medium">#{selectedQuery.id.slice(0, 8)}</p>
                            </div>

                            <div className="space-y-4">
                                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                                    <h3 className="text-xs font-bold text-gray-400 uppercase mb-2 tracking-wider">Status</h3>
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold border ${getStatusColor(selectedQuery.status)}`}>
                                        {selectedQuery.status}
                                    </span>
                                </div>

                                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                                    <h3 className="text-xs font-bold text-gray-400 uppercase mb-2 tracking-wider">Schedule</h3>
                                    <div className="flex flex-col gap-2">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Start</span>
                                            <span className="text-sm font-bold text-gray-900">{formatDateString(selectedQuery.startDate)}</span>
                                        </div>
                                        <div className="w-full h-px bg-gray-50"></div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Due</span>
                                            <span className="text-sm font-bold text-gray-900">{formatDateString(selectedQuery.dueDate)}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                                    <h3 className="text-xs font-bold text-gray-400 uppercase mb-2 tracking-wider">Location</h3>
                                    <p className="text-sm text-gray-800 font-medium leading-snug mb-3">{selectedQuery.location || selectedQuery.address}</p>
                                    {(selectedQuery.latitude || selectedQuery.longitude) && (
                                        <div className="w-full h-32 bg-gray-100 rounded-lg overflow-hidden relative">
                                            <iframe
                                                width="100%"
                                                height="100%"
                                                frameBorder="0"
                                                scrolling="no"
                                                src={`https://maps.google.com/maps?q=${selectedQuery.latitude},${selectedQuery.longitude}&z=15&output=embed`}
                                                title="Location Map"
                                                className="absolute inset-0"
                                            ></iframe>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Main Content / Right Column */}
                        <div className="w-full md:w-2/3 p-6 bg-white overflow-y-auto">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-xl font-bold text-gray-900">Query Details</h2>
                                <button onClick={() => setSelectedQuery(null)} className="text-gray-400 hover:text-gray-600 p-1 bg-gray-100 rounded-full">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>

                            <div className="space-y-6">
                                {/* Description */}
                                <div>
                                    <h3 className="text-sm font-bold text-gray-900 mb-2">Description</h3>
                                    <p className="text-gray-600 text-sm leading-relaxed bg-gray-50 p-4 rounded-xl border border-gray-100">{selectedQuery.description}</p>
                                </div>

                                {/* Driver Info (read-only) */}
                                {selectedQuery.driverId && getWorkflowProgress(selectedQuery.status) >= getWorkflowProgress(QUERY_STATUSES.DRIVER_ASSIGNED) && (
                                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                                        <div className="flex items-center gap-2 mb-3 text-blue-900 font-bold text-sm">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                            Driver Assigned
                                        </div>
                                        <div className="flex justify-between items-center bg-white p-3 rounded-lg border border-blue-200">
                                            <div className="flex items-center">
                                                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mr-3">
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-gray-900">{selectedQuery.driverName}</p>
                                                    <p className="text-xs text-blue-600 font-medium">Driver</p>
                                                </div>
                                            </div>
                                            {selectedQuery.driverPhone && (
                                                <a href={`tel:${selectedQuery.driverPhone}`} className="flex items-center gap-1 bg-green-50 text-green-700 hover:bg-green-100 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                                                    Call
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Inspection Report */}
                                {getWorkflowProgress(selectedQuery.status) >= getWorkflowProgress(QUERY_STATUSES.DRIVER_REPORTED) && (
                                    <div className="pt-4 border-t border-gray-100">
                                        <div className="flex justify-between items-center mb-2">
                                            <h3 className="text-sm font-bold text-gray-900">Inspection Report</h3>
                                            {selectedQuery.driverName && (
                                                <span className="text-xs text-gray-500">by {selectedQuery.driverName}</span>
                                            )}
                                        </div>
                                        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                                            {(selectedQuery.adminInspectionImages || []).map((img, idx) => {
                                                const url = typeof img === 'string' ? img : (img.uri || img.url);
                                                const isPdf = url && url.toLowerCase().endsWith('.pdf');
                                                return url ? (
                                                    <div key={idx} className="relative w-24 h-24 flex-shrink-0 group">
                                                        {isPdf ? (
                                                            <a href={url} target="_blank" rel="noreferrer" className="w-full h-full flex flex-col items-center justify-center bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors">
                                                                <svg className="w-6 h-6 text-red-500 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                                                <span className="text-xs text-red-600 font-medium">PDF</span>
                                                            </a>
                                                        ) : (
                                                            <a href={url} target="_blank" rel="noreferrer">
                                                                <img src={url} className="w-full h-full object-cover rounded-lg border border-gray-200" alt="Inspection" />
                                                            </a>
                                                        )}
                                                    </div>
                                                ) : null;
                                            })}
                                            {(!selectedQuery.adminInspectionImages?.length) && <p className="text-xs text-gray-400 py-2">No inspection files uploaded yet.</p>}
                                        </div>
                                    </div>
                                )}

                                {/* Quotation Section */}
                                {getWorkflowProgress(selectedQuery.status) >= getWorkflowProgress(QUERY_STATUSES.DRIVER_REPORTED) && (
                                    <div className="pt-4 border-t border-gray-100">
                                        <div className="flex justify-between items-center mb-2">
                                            <h3 className="text-sm font-bold text-gray-900">Quotation</h3>
                                            {selectedQuery.status === QUERY_STATUSES.QUOTATION_ACCEPTED && <span className="text-green-600 text-xs font-bold bg-green-50 px-2 py-1 rounded">Accepted</span>}
                                        </div>

                                        {selectedQuery.quotationUrl ? (
                                            <div className="flex flex-col gap-2">
                                                <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-100 rounded-lg">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center text-blue-600"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg></div>
                                                        <a href={selectedQuery.quotationUrl} target="_blank" rel="noreferrer" className="text-sm font-bold text-blue-900 hover:underline">View Quotation PDF</a>
                                                    </div>
                                                </div>

                                                {/* Accept/Reject Actions if Uploaded */}
                                                {selectedQuery.status === QUERY_STATUSES.QUOTATION_UPLOADED && (
                                                    <div className="flex gap-2 mt-1">
                                                        <button
                                                            onClick={handleAcceptQuotation}
                                                            className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 rounded-lg font-bold text-xs"
                                                        >
                                                            Accept Quotation
                                                        </button>
                                                        <button
                                                            onClick={handleRejectQuotation}
                                                            className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg font-bold text-xs"
                                                        >
                                                            Reject
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-gray-400 italic">No quotation uploaded by Admin yet.</p>
                                        )}
                                    </div>
                                )}

                                {/* Completion Reports — Technician work after quotation acceptance */}
                                {getWorkflowProgress(selectedQuery.status) >= getWorkflowProgress(QUERY_STATUSES.TECHNICIAN_COMPLETED) && (
                                    <div className="pt-4 border-t border-gray-100">
                                        <div className="flex justify-between items-center mb-2">
                                            <h3 className="text-sm font-bold text-gray-900">Work Completion Report</h3>
                                            {selectedQuery.technicianName && (
                                                <span className="text-xs text-gray-500">by {selectedQuery.technicianName}</span>
                                            )}
                                        </div>
                                        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                                            {(selectedQuery.adminCompletionImages || []).map((img, idx) => {
                                                const url = typeof img === 'string' ? img : (img.uri || img.url);
                                                const isPdf = url && url.toLowerCase().endsWith('.pdf');
                                                return url ? (
                                                    <div key={idx} className="relative w-24 h-24 flex-shrink-0 group">
                                                        {isPdf ? (
                                                            <a href={url} target="_blank" rel="noreferrer" className="w-full h-full flex flex-col items-center justify-center bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors">
                                                                <svg className="w-6 h-6 text-red-500 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                                                <span className="text-xs text-red-600 font-medium">PDF</span>
                                                            </a>
                                                        ) : (
                                                            <a href={url} target="_blank" rel="noreferrer">
                                                                <img src={url} className="w-full h-full object-cover rounded-lg border border-gray-200" alt="Completion" />
                                                            </a>
                                                        )}
                                                    </div>
                                                ) : null;
                                            })}
                                            {(!selectedQuery.adminCompletionImages?.length) && <p className="text-xs text-gray-400 py-2">No completion files uploaded yet.</p>}
                                        </div>
                                    </div>
                                )}

                                {/* Payment & Invoice Section - Only show after technician completion */}
                                {(getWorkflowProgress(selectedQuery.status) >= getWorkflowProgress(QUERY_STATUSES.TECHNICIAN_COMPLETED) || invoices.length > 0) && (
                                    <div className="pt-4 border-t border-gray-100 pb-2">
                                        <h3 className="text-sm font-bold text-gray-800 mb-2">Payment & Invoice</h3>
                                        <div className="bg-gray-900 text-white rounded-2xl shadow-xl overflow-hidden">
                                            <div className="p-5 flex justify-between items-center relative">
                                                <div className="absolute top-0 right-0 w-32 h-32 bg-gray-800 rounded-full blur-3xl -mr-10 -mt-10 opacity-50"></div>
                                                <div className="relative z-10">
                                                    <div>
                                                        <p className="text-gray-400 text-xs mb-1 font-medium">Total Amount</p>
                                                        <span className="text-2xl font-bold tracking-tight">{selectedQuery.amount || '0'} <span className="text-sm text-gray-500 font-normal">AED</span></span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="bg-gray-800 p-4 border-t border-gray-700">
                                                {invoices.length > 0 ? (
                                                    <div className="space-y-2 mb-4">
                                                        {invoices.map((inv) => (
                                                            <div key={inv.id} className="flex items-center justify-between p-2 bg-gray-700/50 rounded-lg border border-gray-700">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-8 h-8 bg-gray-600 rounded flex items-center justify-center text-gray-300">
                                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                                                    </div>
                                                                    <a href={inv.fileUrl} target="_blank" rel="noreferrer" className="text-xs font-bold text-gray-200 hover:text-white truncate max-w-[150px]">{inv.fileName}</a>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-xs text-gray-500 italic mb-4">No invoices generated yet.</p>
                                                )}

                                                {selectedQuery.paymentProof && (
                                                <div className="pt-4 border-t border-gray-700">
                                                    <p className="text-gray-400 text-xs font-bold uppercase mb-2">Payment Receipt</p>
                                                    <a href={selectedQuery.paymentProof} target="_blank" rel="noreferrer" className="block w-20 h-20">
                                                        <img src={selectedQuery.paymentProof} alt="Receipt" className="w-full h-full object-cover rounded border border-gray-600" />
                                                    </a>
                                                </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* After Service / Follow Up Section */}
                                {((selectedQuery.status === QUERY_STATUSES.PAID || selectedQuery.status === QUERY_STATUSES.CLOSED || selectedQuery.followUp) || getWorkflowProgress(selectedQuery.status) >= 100) && (
                                    <div className="pt-6 border-t border-gray-100">
                                        <div className="flex justify-between items-center mb-4">
                                            <h3 className="text-lg font-bold text-gray-900">After Service</h3>
                                            {selectedQuery.followUp?.status === 'resolved' && (
                                                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-bold">Resolved</span>
                                            )}
                                        </div>

                                        {selectedQuery.followUp ? (
                                            <div className="bg-orange-50 p-4 rounded-xl border border-orange-100">
                                                <div className="flex justify-between items-center mb-2">
                                                    <h4 className="text-sm font-bold text-orange-900">Reported Issue</h4>
                                                    <span className="text-xs text-gray-500">{formatDate(selectedQuery.followUp.createdAt)}</span>
                                                </div>
                                                <p className="text-sm text-gray-800 mb-3">{selectedQuery.followUp.description}</p>

                                                {selectedQuery.followUp.images && selectedQuery.followUp.images.length > 0 && (
                                                    <div className="flex gap-2 overflow-x-auto pb-2 mb-3">
                                                        {selectedQuery.followUp.images.map((img, idx) => (
                                                            <a key={idx} href={img} target="_blank" rel="noreferrer">
                                                                <img src={img} alt="Issue" className="w-16 h-16 object-cover rounded border border-orange-200" />
                                                            </a>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* Admin Reply */}
                                                {selectedQuery.followUp.adminReply && (
                                                    <div className="mt-3 pt-3 border-t border-orange-200">
                                                        <p className="text-xs font-bold text-orange-900 mb-1">Admin Reply</p>
                                                        <p className="text-sm text-gray-700">{selectedQuery.followUp.adminReply}</p>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="text-center py-6 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                                                <p className="text-sm text-gray-400 mb-2">Issue with the completed service?</p>
                                                {canCreateFollowUp(selectedQuery) ? (
                                                    <button
                                                        onClick={() => {
                                                            setReportType('followup');
                                                            setFollowUpImages([]);
                                                            setIsReportModalOpen(true);
                                                        }}
                                                        className="text-orange-600 text-xs font-bold hover:underline"
                                                    >
                                                        Report an Issue
                                                    </button>
                                                ) : (
                                                    <p className="text-xs text-gray-400">{getFollowUpTimeRemaining(selectedQuery)}</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Report Issue Modal - Enhanced for Image Upload */}
            {isReportModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl max-w-md w-full p-6">
                        <h2 className="text-xl font-bold mb-4">{reportType === 'followup' ? 'Report After-Service Issue' : 'Report an Issue'}</h2>
                        <textarea
                            className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-brand-golden outline-none"
                            placeholder="Describe the issue..."
                            rows="4"
                            value={reportDescription}
                            onChange={(e) => setReportDescription(e.target.value)}
                        ></textarea>

                        {/* Image Upload for Query Report */}
                        <div className="mb-4">
                            <label className="block text-sm font-bold text-gray-700 mb-2">Add Photos (Optional)</label>
                            <div className="flex items-center gap-2">
                                <label className="bg-gray-100 hover:bg-gray-200 text-gray-600 px-3 py-2 rounded-lg cursor-pointer text-xs font-bold flex items-center gap-1 transition-colors">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                    {isUploadingReport ? 'Uploading...' : 'Upload Images'}
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/*"
                                        className="hidden"
                                        onChange={handleUploadReportImage}
                                        disabled={isUploadingReport}
                                    />
                                </label>
                                <span className="text-xs text-gray-400">{followUpImages.length} images added</span>
                            </div>

                            {followUpImages.length > 0 && (
                                <div className="flex gap-2 mt-2 overflow-x-auto pb-2">
                                    {followUpImages.map((url, idx) => (
                                        <div key={idx} className="relative w-16 h-16 flex-shrink-0 group">
                                            <img src={url} alt="Proof" className="w-full h-full object-cover rounded border border-gray-200" />
                                            <button
                                                onClick={() => handleDeleteReportImage(url)}
                                                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setIsReportModalOpen(false)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleReportIssue}
                                className="px-4 py-2 bg-brand-golden hover:bg-yellow-600 text-black rounded-lg font-bold"
                                disabled={isUploadingReport}
                            >
                                Submit Report
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Creatae Query Modal - Same as Partner */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-bold mb-4">Create New Query</h2>
                        <form onSubmit={handleCreateQuery} className="space-y-4">
                            {/* Same fields as PartnerQueries */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Service Type</label>
                                <input
                                    type="text"
                                    placeholder="e.g. AC Repair"
                                    value={newQuery.serviceType}
                                    onChange={e => setNewQuery({ ...newQuery, serviceType: e.target.value })}
                                    required
                                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Title</label>
                                <input
                                    type="text"
                                    placeholder="Brief title"
                                    value={newQuery.title}
                                    onChange={e => setNewQuery({ ...newQuery, title: e.target.value })}
                                    required
                                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Description</label>
                                <textarea
                                    value={newQuery.description}
                                    onChange={e => setNewQuery({ ...newQuery, description: e.target.value })}
                                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                    rows="3"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Address / Location</label>
                                <input
                                    type="text"
                                    value={newQuery.address}
                                    onChange={e => setNewQuery({ ...newQuery, address: e.target.value })}
                                    required
                                    className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Start Date</label>
                                    <input
                                        type="date"
                                        value={newQuery.startDate}
                                        onChange={e => setNewQuery({ ...newQuery, startDate: e.target.value })}
                                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Due Date</label>
                                    <input
                                        type="date"
                                        value={newQuery.dueDate}
                                        onChange={e => setNewQuery({ ...newQuery, dueDate: e.target.value })}
                                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Cancel</button>
                                <button type="submit" className="px-4 py-2 text-black bg-brand-golden rounded-md hover:bg-yellow-600">Create Query</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ManagerQueries;
